import { Track } from "@/src/lib/tracking";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { wrapperResult } from "@api/osp";
import { QUERY_KEYS } from "@config/constant";
import { Token, TokenPagination } from "../features/api/openData";
import { cleanNullishParams } from "@utils/cleanParams";
import { CRYPTO_THEME_LABELS } from "@componentsv1/radar/consts/themes";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { TokenSortField, TokenSortOrder } from "@/src/types/api-params";

const PAGE_SIZE = 10;

// 定义 TokensQuestParams 的具体类型
export const TokensQuestParams = {
  EnRich_Market: "enrich.with_market.enable" as const,
  EnRich_Enable: "enrich.enable" as const,
  Enrich_With_SparkLine_Enable: "enrich.with_sparkline.enable" as const,
  Enrich_With_SparkLine_Interval: "enrich.with_sparkline.interval" as const,
  Enrich_With_Rating: "enrich.with_rating" as const,
  Enrich_With_SparkLine_Trading_Period:
    "enrich.with_sparkline.trading_period" as const,
  Sort_Order: "sort.order" as const,
  Sort_Field: "sort.field" as const,
} as const;

// 定义查询参数的精确类型
export type TokensQueryParams = {
  [TokensQuestParams.EnRich_Market]?: boolean;
  [TokensQuestParams.EnRich_Enable]?: boolean;
  [TokensQuestParams.Enrich_With_Rating]?: boolean;
  [TokensQuestParams.Enrich_With_SparkLine_Enable]?: boolean;
  [TokensQuestParams.Sort_Order]?: TokenSortOrder;
  [TokensQuestParams.Sort_Field]?: TokenSortField;
  [TokensQuestParams.Enrich_With_SparkLine_Interval]?: string;
  [TokensQuestParams.Enrich_With_SparkLine_Trading_Period]?: string;
  limit?: number;
  offset?: number;
  type?: string;
  theme?: string;
} & Record<string, any>;

export interface TokenResult extends Token {
  key?: string;
}

export interface TokensRankingResult {
  data: {
    data: TokenResult[];
    total: number;
  };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
  };
  isLoading: boolean;
  error: any;
  isFetching: boolean;
  refetch: () => void;
}

interface TokensRankingParams {
  queryParams?: TokensQueryParams;
  enabled?: boolean;
  homeCategory?: string;
  gcTime?: number;
}
/**
 * Tokens Ranking Hook
 *
 * 基于 useQuery 实现，支持：
 * - 动态 category 参数从 URL params 获取
 * - 翻页功能
 * - 可配置的 enrichment 选项
 *
 * @param params 配置参数
 * @returns Tokens Ranking 查询结果和翻页控制
 */
export const useTokensRanking = (
  params: TokensRankingParams = {},
): TokensRankingResult => {
  const {
    enabled = true,
    queryParams: inputQueryParams,
    homeCategory,
    gcTime = 30 * 1000,
  } = params;
  const { limit } = inputQueryParams || {};
  const { openDataApi } = useOspApiClientProvider();
  const urlParams = useParams();
  // 获取动态 category 参数
  const category = homeCategory || urlParams.dexId;
  const type = category === CRYPTO_THEME_LABELS.blueChip ? "BLUE_CHIP" : null;
  // theme slug
  const theme = category === CRYPTO_THEME_LABELS.blueChip ? null : category;

  // 翻页状态管理
  const [currentPage, setCurrentPage] = useState(1);
  const lastCategoryRef = useRef(category);
  const pageLimit = limit || PAGE_SIZE;
  const availablePage = lastCategoryRef.current !== category ? 1 : currentPage;

  // 当 category 变化时重置页码
  useEffect(() => {
    if (lastCategoryRef.current !== category) {
      setCurrentPage(1);
      lastCategoryRef.current = category;
    }
  }, [category]);

  // 构建查询参数
  const defaultQueryParams = {
    "enrich.with_market.enable": true,
    "enrich.enable": true,
    "enrich.with_rating": true,
    offset: (availablePage - 1) * pageLimit,
    limit: pageLimit,
  };

  const initialPageParam = cleanNullishParams({
    ...defaultQueryParams,
    ...inputQueryParams,
    ...(type && { type }),
    ...(theme && { themes: [theme] }),
  });

  // API 查询
  const result = useQuery({
    enabled: !!openDataApi && enabled,
    staleTime: gcTime,
    queryKey: [QUERY_KEYS.TOKENS_RANKING, initialPageParam, currentPage],
    queryFn: async (): Promise<TokenPagination> => {
      try {
        const data = await wrapperResult(
          openDataApi!.tokens.getTokensRanking(
            initialPageParam as {
              type?: "BLUE_CHIP" | "REPORT_360";
              [k: string]: unknown;
            },
          ),
        );

        return data as TokenPagination;
      } catch (error) {
        Track.log(
          "error",
          "useTokensRanking",
          "Failed to fetch tokens ranking:",
          { cause: String(error) },
        );
        throw new Error("Failed to fetch tokens ranking");
      }
    },
  });

  // 翻页处理
  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // 数据处理
  const selectData = useMemo(() => {
    return (result.data?.rows || []).map((token: any, index: number) => {
      const priceChangeMap = new Map(
        token?.market_data?.price_change_percentage?.map((item: any) => [
          item.time_period,
          item.percentage,
        ]) || [],
      );
      return {
        ...token,
        key: `${token.address || token.symbol || token.name || "token"}-${index}`,
        trade: {
          price_usdt: token?.market_data?.current_price,
          price_change_1h: priceChangeMap?.get("1h"),
          price_change_24h: priceChangeMap?.get("24h"),
          price_change_7d: priceChangeMap?.get("7d"),
          price_change_14d: priceChangeMap?.get("14d"),
          price_change_30d: priceChangeMap?.get("30d"),
          price_change_200d: priceChangeMap?.get("200d"),
          vol_24h_usdt: token?.market_data?.volume_24h,
          turnover_rate: token?.market_data?.turnover_rate,
        },
        market_cap: token?.market_data?.market_cap,
        fdv: token?.market_data?.fdv,
        socials: {
          image_url: token?.logo,
          ...token?.social_media,
        },
      } as TokenResult;
    });
  }, [result.data?.rows]);

  return {
    ...result,
    data: {
      data: selectData,
      total: result.data?.total || 0,
    },
    pagination: {
      current: availablePage,
      pageSize: pageLimit,
      total: result.data?.total || 0,
      onChange: onPageChange,
    },
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
};
